import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { Avviso, Campo, Pulsante } from '../../componenti';
import { ErroreApi, ErroreRete } from '../../lib/api';
import { ora } from '../../lib/formato';
import { useSessione } from '../../stato/Sessione';

/**
 * 6g, 6h — schermo bloccato per inattività.
 *
 * 30 minuti su mobile e tablet, 15 su desktop. Si sblocca con la sola password e la
 * sessione non si perde: quanto non è ancora stato salvato resta nel client. Il blocco non
 * prolunga la sessione — la scadenza resta quella originaria.
 *
 * Chi ha un'accettazione in corso vede che è conservata (6g): la bozza vive nel client e
 * non è ancora un record. Cambiando utente si perde, e va detto prima.
 */

interface Props {
  /** Descrizione di ciò che il client sta conservando, se qualcosa. */
  lavoroInCorso?: ReactNode;
}

export function Blocco({ lavoroInCorso }: Props) {
  const { profilo, sblocca, esci } = useSessione();
  const [password, setPassword] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  if (!profilo) return null;

  const nome = profilo.tiratore
    ? `${profilo.tiratore.nome} ${profilo.tiratore.cognome}`
    : profilo.username;

  const ruolo =
    profilo.ruolo === 'dt'
      ? 'DT'
      : profilo.ruolo === 'admin'
        ? 'amministratore'
        : 'tiratore';

  async function invia(e: FormEvent) {
    e.preventDefault();
    if (inCorso) return;
    setInCorso(true);
    setErrore(null);
    try {
      await sblocca(password);
      setPassword('');
    } catch (err) {
      if (err instanceof ErroreRete) setErrore('Serve la connessione. Riprova.');
      else if (err instanceof ErroreApi) setErrore('La password non è corretta.');
      else throw err;
    } finally {
      setInCorso(false);
    }
  }

  const densita = profilo.dispositivo === 'mobile' ? 'tattile' : 'compatta';

  return (
    <div className="velo blocco__velo" data-densita={densita}>
      <div className="blocco" role="dialog" aria-modal="true">
        <div>
          <div className="blocco__titolo">Schermo bloccato</div>
          {/* I mockup scrivono «inattivo»/«inattiva» concordando con la persona; il genere
              non è un dato che l'applicazione conosce, quindi si usa una forma neutra. */}
          <div className="blocco__chi">
            {nome} · {ruolo} · senza attività da {profilo.minuti_blocco} minuti
          </div>
        </div>

        {lavoroInCorso ? (
          <div className="blocco__conservato">
            <span className="blocco__segno" aria-hidden="true">
              ✓
            </span>
            <div>{lavoroInCorso}</div>
          </div>
        ) : (
          <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--testo-secondario)' }}>
            La sessione è ancora aperta. Inserisci la password per tornare esattamente dove
            eri: quanto avevi aperto e i filtri impostati non si perdono.
          </p>
        )}

        {errore ? <Avviso tono="errore" titolo={errore} /> : null}

        <form onSubmit={invia} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Campo
            etichetta={`Password di ${nome}`}
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="blocco__azioni">
            <Pulsante type="submit" largo grande disabled={inCorso}>
              {lavoroInCorso ? 'Sblocca e riprendi' : 'Sblocca'}
            </Pulsante>
            <Pulsante variante="neutro" largo onClick={() => void esci()}>
              {lavoroInCorso ? 'Cambia utente' : 'Esci'}
            </Pulsante>
          </div>
        </form>

        <div className="blocco__piede">
          {lavoroInCorso
            ? 'Cambiando utente l’accettazione in corso viene persa: il tiratore dovrà mostrare di nuovo il QR.'
            : `La sessione scade comunque alle ${ora(profilo.scade_il)}.`}
        </div>
      </div>
    </div>
  );
}
